import {User, UserSchema} from '../schema/User';

/**
 * Determines if a user should be redirected to the onboarding flow
 * based on whether they have completed their profile information.
 *
 * Checks if the user is missing any of these essential profile fields:
 * - firstName
 * - birthDate
 * - gender
 * - location
 * - content creator or influencer status
 * - specialties (tags)
 * - capabilities (content preferences)
 * - bio
 * - portfolio media items
 * - social identities
 *
 * @param user The user object to check
 * @returns true if the user should be redirected to onboarding, false otherwise
 */
// eslint-disable-next-line import/prefer-default-export
export const shouldRedirectToOnboarding = (
  inputUser: User | undefined | null
): boolean => {
  const parsedUser = UserSchema.safeParse(inputUser);
  if (!parsedUser.success) {
    return false;
  }

  const user = parsedUser.data;

  if (!user) return false;

  // Check if firstName is missing or empty
  const isFirstNameMissing = !user.firstName || user.firstName.trim() === '';

  // Check if lastName is missing or empty
  const isLastNameMissing = !user.lastName || user.lastName.trim() === '';

  // Check if birthDate is missing
  const isBirthDateMissing = !user.birthDate;

  // Check if gender is missing
  const isGenderMissing = !user.profile?.gender;

  // Check if location is missing
  const isLocationMissing = !user.profile?.location && !user.profile?.locale;

  // Check if user has specialties (tags)
  const hasNoSpecialties =
    !user.profile?.tags || user.profile.tags.length === 0;

  // Check if user has capabilities (content preferences)
  const hasNoCapabilities =
    !user.profile?.contentPreferences ||
    user.profile.contentPreferences.length === 0;

  // Check if collaboration rate is missing
  const isMinCollaborationRateMissing =
    user.profile?.minCollaborationRate === undefined;

  // Check if bio is missing or empty
  const isBioMissing = !user.profile?.bio || user.profile.bio.trim() === '';

  // Check if social identities are missing
  const isInfluencer = user.profile?.influencer === true;
  const isInfluencerWithoutIdentities =
    isInfluencer && (!user.identities || user.identities.length === 0);

  const isContentCreator = user.profile?.contentCreator === true;
  const hasNoPortfolioItems =
    !user.profile?.portfolioMediaItems ||
    user.profile.portfolioMediaItems.length === 0;

  const isCreatorStatusMissing = !isContentCreator && !isInfluencer;

  // Return true if any of the essential fields are missing
  return (
    isFirstNameMissing ||
    isLastNameMissing ||
    isBirthDateMissing ||
    isGenderMissing ||
    isLocationMissing ||
    isCreatorStatusMissing ||
    hasNoSpecialties ||
    hasNoCapabilities ||
    isBioMissing ||
    isMinCollaborationRateMissing ||
    hasNoPortfolioItems ||
    isInfluencerWithoutIdentities
  );
};
